import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";

import {
  ICreateProductCategoryReq,
  IEditProductCategoryReq,
  IGetAllProductCategoryReq,
  IDeleteProductCategoryReq,
} from "@evennt-tech/api-v2-external";
import {
  IProductCategoryWithName,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { registerStore } from "store/utils/reset-store.utils";
import { IProductCategoryForAdmin } from "ts/api/productCategory";

type State = {
  items: IProductCategoryWithName[];
  itemsForAdmin: IProductCategoryForAdmin[];
  isLoading: boolean;
  error: string | null;
};

type Action = {
  fetchManyByAdmin: () => Promise<void>;
  fetchAll: (params?: IGetAllProductCategoryReq) => Promise<void>;
  create: (body: ICreateProductCategoryReq) => Promise<void>;
  edit: (body: IEditProductCategoryReq) => Promise<void>;
  delete: (body: IDeleteProductCategoryReq) => Promise<void>;
  setError: (message: string | null) => void;
  reload: () => Promise<void>;
  autoCatch: () => void;
  reloadAll: () => Promise<void>;
  autoCatchAll: () => void;
};

export const useProductCategoryStore = create<State & Action>()(
  devtools((set, get) => ({
    items: [],
    itemsForAdmin: [],
    isLoading: false,
    error: null,

    setError: (message) => set({ error: message }),

    fetchManyByAdmin: async () => {
      set({ isLoading: true });
      try {
        const res = await apiV2.productCategory.getMany();
        const itemsWithNested = res.map((category) => ({
          ...category,
          nested: [...(category.subCategories ?? [])].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
        }));

        set({
          itemsForAdmin: itemsWithNested,
          error: null,
        });
      } catch (e) {
        console.error("Failed to fetch product categories (admin):", e);
        set({ error: getErrorMessage(e) });
      } finally {
        set({ isLoading: false });
      }
    },

    reload: async () => {
      await get().fetchManyByAdmin();
    },

    autoCatch: () => {
      const { reload } = get();
      const keys = [
        NotificationType.PRODUCT_CATEGORY_CREATED,
        NotificationType.PRODUCT_CATEGORY_EDITED,
        NotificationType.PRODUCT_CATEGORY_DELETED,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: "product_category_for_admin",
          callback: () => reload(),
        });
      }
    },

    reloadAll: async () => {
      const { fetchAll } = get();
      await fetchAll({ mainOnly: true });
    },

    autoCatchAll: () => {
      const { reloadAll } = get();
      const keys = [
        NotificationType.PRODUCT_CATEGORY_CREATED,
        NotificationType.PRODUCT_CATEGORY_EDITED,
        NotificationType.PRODUCT_CATEGORY_DELETED,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: "product_category",
          callback: () => reloadAll(),
        });
      }
    },

    fetchAll: async (params) => {
      set({ isLoading: true });
      try {
        const res = await apiV2.productCategory.getAll(params ?? {});
        set({ items: res ?? [], error: null });
      } catch (e) {
        console.error("Failed to fetch all product categories:", e);
        set({ error: getErrorMessage(e) });
      } finally {
        set({ isLoading: false });
      }
    },

    create: async (body) => {
      set({ isLoading: true });
      try {
        await apiV2.productCategory.create(body);
      } catch (e) {
        console.error("Failed to create product category:", e);
        set({ error: getErrorMessage(e) });
        throw getErrorMessage(e);
      } finally {
        set({ isLoading: false });
      }
    },

    edit: async (body) => {
      set({ isLoading: true });
      try {
        await apiV2.productCategory.edit(body);
      } catch (e) {
        console.error("Failed to edit product category:", e);
        set({ error: getErrorMessage(e) });
        throw getErrorMessage(e);
      } finally {
        set({ isLoading: false });
      }
    },

    delete: async (body) => {
      set({ isLoading: true });
      try {
        await apiV2.productCategory.delete(body);
      } catch (e) {
        console.error("Failed to delete product category:", e);
        set({ error: getErrorMessage(e) });
        throw getErrorMessage(e);
      } finally {
        set({ isLoading: false });
      }
    },
  }))
);

registerStore(useProductCategoryStore);
